import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, flatMap, map, mergeMap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { NgRedux } from '@redux/redux-compatibility.service';

import { AppState, actionsList } from '../redux/store';
import { AuthService } from './auth.service';
import { toCamelCaseProperties, toSnakeCaseProperties } from '../utils';

import { Config } from 'src/app/config';
import { ConfigScope, IIntegrationTypeSchema, IIntegrationType, IntegrationType, ICatalogPublishResult } from '../models/integration-type.model';
import { ServiceCallInfo } from '../models/common';

import { SharedService } from 'src/app/shared/services/shared.service';
import { TranslateService } from '@ngx-translate/core';
import { BoundingBox, CatDoc, ReferenceDocument } from '@shared/models/cat.model';

@Injectable({
  providedIn: 'root'
})
export class IntegrationService {
  mockupSw = true;
  scaiServiceUrl: string;
  
  private mockupDataUrl = 'assets/mock/';

  constructor(
    private ngRedux: NgRedux<AppState>,
    private http: HttpClient,
    private authService: AuthService,
    private sharedService: SharedService,
    private translateService: TranslateService
  ) {
    this.scaiServiceUrl = Config.assetConfigData['scaiServiceUrl'];
  }

  get curCatIntegrationConfig(): any {
    return this.ngRedux.getState().curCatIntegrationConfig;
  }

  getIntegrationTypes(envId?: number, shouldUpdateStore: boolean = true): Observable<IIntegrationType[]> {
    const url = this.scaiServiceUrl + `catalogs/integration/types?env_id=${envId}`;

    return this.http
      .get(url, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json'
      })
      .pipe(
        map((data) => {
          data = toCamelCaseProperties(data);

          return this.extractIntegrationTypes(data, shouldUpdateStore);
        }),
        catchError((err) => {
          return this.handleError(err, url)
        })
      );
  }

  private extractIntegrationTypes(res, shouldUpdateStore: boolean = true): IIntegrationType[] {
    const integrationTypes: IIntegrationType[] = res.integrationTypes;
    const configuredIntegrationType: IIntegrationType = this.extractConfiguredIntegrationType(integrationTypes);

    if (shouldUpdateStore) {
      this.ngRedux.dispatch({
        type: actionsList.GET_INTEGRATION_TYPES,
        data: integrationTypes,
        reply: res
      });

      this.ngRedux.dispatch({
        type: actionsList.CONFIGURED_INTEGRATION_TYPE,
        data: configuredIntegrationType ? configuredIntegrationType.type : null,
        reply: integrationTypes
      });
    }

    return integrationTypes;
  }

  private extractConfiguredIntegrationType(integrationTypes: IIntegrationType[]): IIntegrationType {
    return integrationTypes ? integrationTypes.find((integrationType: IIntegrationType) => {
      return integrationType.isConfigured;
    }) : null;
  }

  getIntegrationConfig(
    integrationType: string,
    configScope = ConfigScope.Library,
    environmentId?: number,
    entityId?: number
  ): Observable<IIntegrationTypeSchema[]> {
    const url = this.scaiServiceUrl + `catalogs/integrations/${integrationType}/${configScope}/schema?env_id=${environmentId}&entity_id=${entityId}`;

    return this.http
      .get(url, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json'
      })
      .pipe(
        map((data) => {
          data = toCamelCaseProperties(data);

          return this.extractIntegrationConfig(data, configScope);
        }),
        catchError((err) => {
          return this.handleError(err, url)
        })
      );
  }

  private extractIntegrationConfig(res, configScope: ConfigScope): IIntegrationTypeSchema[] {
    const result: IIntegrationTypeSchema[] = res.fields;
    let action: string;

    result.forEach(item => {
      // Since configuration contains data, map data as default values
      item.defaultValue = item.configuredValue ?? item.defaultValue;
      // Convert to boolean
      item.isRequired = typeof item.isRequired == "boolean" ? item.isRequired : (item.isRequired as any) === 'Y';
      item.isSecret = typeof item.isSecret == "boolean" ? item.isSecret : (item.isSecret as any) === 'Y';
      item.isVisible = typeof item.isVisible == "boolean" ? item.isVisible : (item.isVisible as any) === 'Y';
    });

    switch (configScope) {
      case ConfigScope.Library:
        this.ngRedux.dispatch({
          type: actionsList.GET_CATALOG_INTEGRATION_CONFIG,
          data: result,
          reply: res,
        });

        break;
    }

    return result;
  }

  saveIntegrationConf(config: IIntegrationTypeSchema[], formData: any, entityId: number): Observable<boolean> {
    const url = this.scaiServiceUrl + 'catalogs/integration/config';
    const data = {
      field_values: config.map((fieldConfig: IIntegrationTypeSchema) => {
        return {
          id: fieldConfig.id,
          entityId: entityId,
          value: formData[fieldConfig.key],
          isSecret: fieldConfig.isSecret ? 'Y' : 'N'
        };
      })
    };
    const submitData = toSnakeCaseProperties(data);

    return this.http
      .post(url, submitData, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        observe: 'response',
        responseType: 'json'
      })
      .pipe(
        map((response) => {
          if (this.curCatIntegrationConfig) {
            this.curCatIntegrationConfig.forEach((item) => {
              const fieldConfig = config.find((fieldConfig: IIntegrationTypeSchema) => {
                return fieldConfig.id === item.id;
              });

              if (fieldConfig) {
                item.configuredValue = formData[fieldConfig.key];
              }
            })

            this.extractIntegrationConfig({fields: this.curCatIntegrationConfig}, ConfigScope.Library);
          }

          return response.status == 201;
        }),
        catchError((err) => {
          return this.handleError(err, url, true)
        })
      );
  }

  private extractSaveIntegrationConf(submitData, response): void {
    this.ngRedux.dispatch({
      type: actionsList.GET_CATALOG_INTEGRATION_CONFIG,
      data: submitData,
      reply: response,
    });
  }

  addCatalogIntegrationType(environmentId: number, integrationType: string): Observable<boolean> {
    const url = this.scaiServiceUrl + `environments/${environmentId}/catalogs/integration/type`;
    const submitData = {
      integration_type: integrationType
    };

    return this.http
      .post(url, submitData, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        observe: 'response',
        responseType: 'json'
      })
      .pipe(
        map((response) => {
          return response.status == 201;
        }),
        catchError((err) => {
          return this.handleError(err, url)
        })
      );
  }

  deleteCatalogIntegrationType(environmentId: number): Observable<boolean> {
    const url = this.scaiServiceUrl + `environments/${environmentId}/catalogs/integration/type`;

    return this.http
      .delete(url, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        observe: 'response'
      })
      .pipe(
        map((response) => {
          return response.status == 200;
        }),
        catchError((err) => {
          return this.handleError(err, url)
        })
      );
  }

  publishCatalogVersion(catalogVersionId: number): Observable<ICatalogPublishResult> {
    const url = this.scaiServiceUrl + `catalogs/${catalogVersionId}/publish-catalog-version`;

    return this.http
      .post(url, {}, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json'
      })
      .pipe(
        map((response) => {
          return toCamelCaseProperties(response);
        }),
        catchError((err) => {
          return this.handleError(err, url)
        })
      );
  }

  publishCatalogVersionExternal(catalogVersionId: number, envId: number = null): Observable<ICatalogPublishResult> {
    const url = this.scaiServiceUrl + `catalogs/${catalogVersionId}/initiate-publish-external`;

    return this.http
      .post(url, { envId }, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json'
      })
      .pipe(
        map((response) => {
          return toCamelCaseProperties(response);
        }),
        catchError((err) => {
          return this.handleError(err, url)
        })
      );
  }

  pullExternal(catalogVersionId: number, entityId?: number): Observable<ICatalogPublishResult> {
    const url = this.scaiServiceUrl + `catalogs/${catalogVersionId}/init-pull-claims`;

    return this.http
      .post(url, {}, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json'
      })
      .pipe(
        map((response) => {
          let infoMsg = this.translateService.instant(
            'translate.alert.externalPullStarted'
          );
          this.sharedService.alertInfo(infoMsg);
          return toCamelCaseProperties(response);
        }),
        catchError((err) => {
          return this.handleError(err, url)
        })
      );
  }

  finalizePrecheck(documentId: number, statusCode: string = 'FINAL'): Observable<any> {
    const url = this.scaiServiceUrl + `/precheck/initiate-finalize/${documentId}`;

    return this.http
      .post(url, { userId: this.authService.currentUser.userId, statusCode }, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json'
      })
      .pipe(
        map((response) => {
          return toCamelCaseProperties(response);
        }),
        catchError((err) => {
          return this.handleError(err, url)
        })
      );
  }

  addDocumentToLibrary(catalogVersionId: number, documentId: number, assetType: string): Observable<ICatalogPublishResult> {
    let url: string;
  
    if (typeof assetType === 'string' && (assetType.startsWith('Reference') || assetType === 'SMPC')) {
      url = `${this.scaiServiceUrl}catalog/${catalogVersionId}/ref-docs/${documentId}/add`;
    } else {
      url = `${this.scaiServiceUrl}catalogs/${catalogVersionId}/add_to_library/${documentId}`;
    }
  
    return this.http
      .post(url, {}, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json'
      })
      .pipe(
        map((response) => {
          return toCamelCaseProperties(response);
        }),
        catchError((err) => {
          return this.handleError(err, url);
        })
      );
  }

  removeDocumentFromLibrary(catalogVersionId: number, documentId: number, assetType: string): Observable<ICatalogPublishResult> {
    let url: string;
    
    if (typeof assetType === 'string' && assetType.startsWith('Reference')) {
      url = `${this.scaiServiceUrl}catalog/${catalogVersionId}/ref-docs/${documentId}/detach`;
    } else {
      url = this.scaiServiceUrl + `catalogs/${catalogVersionId}/remove_from_library/${documentId}`;
    }
    return this.http
      .post(url, { userId: this.authService.currentUser.userId }, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json'
      })
      .pipe(
        map((response) => {
          return toCamelCaseProperties(response);
        }),
        catchError((err) => {
          return this.handleError(err, url)
        })
      );
  }

  getAvailableReferenceDocs(catalogVersionId: number): Observable<ReferenceDocument[]> {
    const url = `${this.scaiServiceUrl}catalogs/${catalogVersionId}/reference-docs`;
    return this.http
      .get<any[]>(url, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json'
      })
      .pipe(
        map((response) => {
          const referenceDocuments: ReferenceDocument[] = [];
          const refDocsResponse = toCamelCaseProperties(response);
          for (const refDoc of refDocsResponse) {
            const catDoc = new ReferenceDocument();
            catDoc.docId = refDoc.docId;
            catDoc.docName = refDoc.docName;
            catDoc.docType = refDoc.docType;
            catDoc.pagesNum = refDoc.pageNums;
            catDoc.parentDocId = refDoc.parentDocId;
            catDoc.runNum = refDoc.runNum;
            catDoc.versionId = refDoc.versionId;
            catDoc.productId = refDoc.productId;
            catDoc.docS3Key = refDoc.docS3Key;
            catDoc.statusCode = refDoc.indDocStatusCode;
            catDoc.statusDate = refDoc.indDocStatusDate;
            catDoc.fileName = refDoc.fileName;
            catDoc.createDate = refDoc.crDate;
            catDoc.catVersionId = refDoc.catVersionId;
            catDoc.mappedReferenceTexts = refDoc.mappedReferenceTexts || [];
            referenceDocuments.push(catDoc);
          }
          return referenceDocuments;
        }),
        catchError((err) => {
          return this.handleError(err, url);
        })
      );
  }

  getAnchorsForDoc(docId: number, catVersionId: number): Observable<any[]> {
    const url = `${this.scaiServiceUrl}catalogs/${catVersionId}/reference-docs/${docId}/anchors`;
    return this.http
      .get<any[]>(url, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json'
      })
      .pipe(
        map((response) => {
          return toCamelCaseProperties(response);
        }),
        catchError((err) => {
          return this.handleError(err, url);
        })
      );
  }

  reopenPrecheckDocument(catalogVersionId: number, documentId: number): Observable<any> {
    const url = this.scaiServiceUrl + `precheck/${catalogVersionId}/reopen_precheck_document/${documentId}`;

    return this.http
      .post(url, { userId: this.authService.currentUser.userId }, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json'
      })
      .pipe(
        map((response) => {
          return toCamelCaseProperties(response);
        }),
        catchError((err) => {
          return this.handleError(err, url)
        })
      );
  }

  reopenLibraryDocument(catalogVersionId: number, documentId: number): Observable<ICatalogPublishResult> {
    const url = this.scaiServiceUrl + `catalogs/${catalogVersionId}/reopen_library_document/${documentId}`;

    return this.http
      .post(url, { userId: this.authService.currentUser.userId }, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json'
      })
      .pipe(
        map((response) => {
          return toCamelCaseProperties(response);
        }),
        catchError((err) => {
          return this.handleError(err, url)
        })
      );
  }

  approveAll(catalogVersionId: number, documentId: number): Observable<ICatalogPublishResult> {
    const url = this.scaiServiceUrl + `catalogs/${catalogVersionId}/approve_all/${documentId}`;

    return this.http
      .post(url, { userId: this.authService.currentUser.userId, statusCode: 'APPROVE' }, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json'
      })
      .pipe(
        map((response) => {
          return toCamelCaseProperties(response);
        }),
        catchError((err) => {
          return this.handleError(err, url)
        })
      );
  }

  completeDoc(catalogVersionId: number, documentId: number): Observable<any> {
    const url = this.scaiServiceUrl + `precheck/${catalogVersionId}/complete_doc/${documentId}`;

    return this.http
      .post(url, { userId: this.authService.currentUser.userId }, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json'
      })
      .pipe(
        map((response) => {
          return toCamelCaseProperties(response);
        }),
        catchError((err) => {
          return this.handleError(err, url)
        })
      );
  }

  setBlockStatus(cardId: number, cardStatus: string): Observable<ICatalogPublishResult> {
    const url = this.scaiServiceUrl + `block-status?cardId=${cardId}&cardStatus=${cardStatus}`;

    return this.http
      .put(url, {}, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json'
      })
      .pipe(
        map((response) => {
          return toCamelCaseProperties(response);
        }),
        catchError((err) => {
          return this.handleError(err, url)
        })
      );
  }

  setClaimBlockPhraseContentType(cardId: number, isDelete: boolean): Observable<ICatalogPublishResult> {
    const url = this.scaiServiceUrl + `block-claim-phrase-content-type?cardId=${cardId}`;

    const req = isDelete
    ? this.http.delete(url, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json',
      })
    : this.http.post(url, {}, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json',
      });

    return req.pipe(
      map((response) => toCamelCaseProperties(response)),
      catchError((err) => this.handleError(err, url))
    );
  }
    resolveBoundingBoxAnchors(boundingBox: BoundingBox, pageNum: number, docId: number, catVersionId: number): Observable<any> {

      const url = `${this.scaiServiceUrl}catalogs/${catVersionId}/reference-docs/${docId}/anchors/resolve`;
      return this.http.post(url, {
        pageNum: pageNum,
        boundingBox: {
          x1: boundingBox.x1,
          x2: boundingBox.x2,
          y1: boundingBox.y1,
          y2: boundingBox.y2
        }
      }, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: 'json',
      }).pipe(map((data) => {
        
        return data
      }), catchError((err) => {
        throw err;
      })
      );
    }

  private handleError(error: any, url: string, showPopupAlert = false): Observable<never> {
    const info = new ServiceCallInfo(url, url, null);
    error.message = error.error.description || error.statusText;

    return this.authService.handleError(error, info, showPopupAlert);
  }

}

